import React from 'react'
import { CardCP } from 'submodules/nerit-framework-ui/common/components/card/CardCP'
import { ColumnCP } from 'submodules/nerit-framework-ui/common/components/grid/column/ColumnCP'
import { RowCP } from 'submodules/nerit-framework-ui/common/components/grid/row/RowCP'
import { EmptyCP } from 'submodules/nerit-framework-ui/common/components/empty/EmptyCP'
import { QAQCItemResponseDTO } from 'submodules/nerit-aquatec-sdk/services/qaqc/dtos/response/QAQCItemResponseDTO'
import { QAQCExecutionSummary } from 'submodules/nerit-aquatec-sdk/services/qaqc/interfaces/IQAQCExecutionResult'
import { CardHighChartCP } from 'modules/reports/components/charts/card-chart/CardHighChartCP'
import { HighChartsUtils } from 'modules/reports/components/charts/card-chart/inner/HighChartsUtils'
import { QAQCApprovalDonutChartCP } from 'modules/reports/screens/screen-charts/content/qaqc/inner/QAQCApprovalDonutChartCP'
import { ISummaryRowTP } from 'modules/reports/screens/screen-charts/content/qaqc/inner/QAQCDashboardUtils'
import { QAQCSummaryTableCP } from 'modules/reports/screens/screen-charts/content/qaqc/inner/QAQCSummaryTableCP'
import { ThemeProject } from 'config/theme/project/ThemeProject'

interface ICPProps {
	dto: QAQCItemResponseDTO
}

const VALIDATION_CATEGORIES = ['Dissolv/Total', 'LQ', 'DQO/DBO', 'OD', 'STD/CE'] as const

/**
 * Dashboard de Avaliação de Anomalias: resumo, barras agrupadas e donuts por validação.
 */
export function DrawerQAQCAnomalyEvaluationCP(props: ICPProps): JSX.Element {

	const summary = props.dto.result?.summary

	if (!summary) {
		return <EmptyCP description={'Nenhum resultado disponível para esta execução.'} />
	}

	const generalTotals = buildGeneralTotals(summary)
	const evaluatedTotals = buildEvaluatedTotals(summary)
	const validationBarData = buildValidationBarData(summary)
	const donutConfigs = buildDonutConfigs(summary)

	return (
		<>
			<RowCP>
				<ColumnCP md={12}>
					<CardCP
						title={'Quantidade de amostras avaliadas'}
						bordered={true}
						innerSpacing={'none'}
					>
						<RowCP>
							<ColumnCP md={12}>
								<QAQCSummaryTableCP data={generalTotals} />
							</ColumnCP>
							<ColumnCP md={12}>
								<QAQCSummaryTableCP data={evaluatedTotals} />
							</ColumnCP>
						</RowCP>
					</CardCP>
				</ColumnCP>
				<ColumnCP md={12}>
					<CardHighChartCP
						cardTitle={'Quantidade de amostras aprovadas/reprovadas em cada validação'}
						buttonsToHide={['linearScale', 'chartType', 'fixedRatioSize', 'showLabels']}
						chartType={'column'}
						chartOptions={{
							chart: HighChartsUtils.getChart('column'),
							title: { text: '' },
							xAxis: {
								categories: [...VALIDATION_CATEGORIES],
							},
							yAxis: {
								min: 0,
								title: { text: '' },
							},
							legend: {
								align: 'right',
								verticalAlign: 'middle',
								layout: 'vertical',
							},
							series: [
								{
									type: 'column',
									name: 'Aprovados',
									data: validationBarData.approved,
									color: ThemeProject.primary100,
								},
								{
									type: 'column',
									name: 'Reprovados',
									data: validationBarData.rejected,
									color: ThemeProject.primary400,
								},
							],
							plotOptions: {
								column: {
									grouping: true,
									dataLabels: {
										enabled: true,
									},
								},
							},
							tooltip: {
								shared: true,
							},
							exporting: {
								enabled: true,
							},
						}}
					/>
				</ColumnCP>
			</RowCP>

			<RowCP>
				{donutConfigs.slice(0, 3).map((config) => (
					<ColumnCP key={config.key} md={8}>
						<QAQCApprovalDonutChartCP
							cardTitle={config.cardTitle}
							approved={config.approved}
							rejected={config.rejected}
						/>
					</ColumnCP>
				))}
			</RowCP>

			<RowCP>
				{donutConfigs.slice(3, 6).map((config) => (
					<ColumnCP key={config.key} md={8}>
						<QAQCApprovalDonutChartCP
							cardTitle={config.cardTitle}
							approved={config.approved}
							rejected={config.rejected}
						/>
					</ColumnCP>
				))}
			</RowCP>
		</>
	)
}

function buildGeneralTotals(summary: QAQCExecutionSummary): ISummaryRowTP[] {
	return [
		{ key: 'total', label: 'Total de amostras', value: summary.totalSamples ?? 0 },
		{ key: 'approved', label: 'Total de amostras aprovadas', value: summary.totalApprovedSamples ?? 0 },
		{ key: 'rejected', label: 'Total de amostras reprovadas', value: summary.totalRejectedSamples ?? 0 },
	]
}

function buildEvaluatedTotals(summary: QAQCExecutionSummary): ISummaryRowTP[] {
	return [
		{
			key: 'dissolvedAndTotal',
			label: 'Avaliado em Dissv/Total',
			value: summary.dissolvedAndTotal?.totalDissolvedAndTotalEvaluatedSamples ?? 0,
		},
		{
			key: 'lq',
			label: 'Avaliado em LQ',
			value: summary.lQValues?.totalLQEvaluatedSamples ?? 0,
		},
		{
			key: 'dqoDbo',
			label: 'Avaliado em DQO/DBO',
			value: summary.dQOandDBO?.totalDQOandDBOEvaluatedSamples ?? 0,
		},
		{
			key: 'od',
			label: 'Avaliado em OD',
			value: summary.dissolvedOxygen?.totalDissolvedOxEvaluatedSamples ?? 0,
		},
		{
			key: 'stdCe',
			label: 'Avaliado em STD/CE',
			value: summary.dissolvedSolidsToEC?.totalDissolvedSolidsEvaluatedSamples ?? 0,
		},
	]
}

function buildValidationBarData(summary: QAQCExecutionSummary): { approved: number[]; rejected: number[] } {
	return {
		approved: [
			summary.dissolvedAndTotal?.totalDissolvedAndTotalApprovedSamples ?? 0,
			summary.lQValues?.totalLQApprovedSamples ?? 0,
			summary.dQOandDBO?.totalDQOandDBOApprovedSamples ?? 0,
			summary.dissolvedOxygen?.totalDissolvedOxApprovedSamples ?? 0,
			summary.dissolvedSolidsToEC?.totalDissolvedSolidsApprovedSamples ?? 0,
		],
		rejected: [
			summary.dissolvedAndTotal?.totalDissolvedAndTotalRejectedSamples ?? 0,
			summary.lQValues?.totalLQRejectedSamples ?? 0,
			summary.dQOandDBO?.totalDQOandDBORejectedSamples ?? 0,
			summary.dissolvedOxygen?.totalDissolvedOxRejectedSamples ?? 0,
			summary.dissolvedSolidsToEC?.totalDissolvedSolidsRejectedSamples ?? 0,
		],
	}
}

interface IDonutConfigTP {
	key: string
	cardTitle: string
	approved: number
	rejected: number
}

function buildDonutConfigs(summary: QAQCExecutionSummary): IDonutConfigTP[] {
	return [
		{
			key: 'general',
			cardTitle: 'Porcentagem Amostras Aprovadas',
			approved: summary.totalApprovedSamples ?? 0,
			rejected: summary.totalRejectedSamples ?? 0,
		},
		{
			key: 'dissolvedAndTotal',
			cardTitle: 'Porcentagem aprovação Dissolv/Total',
			approved: summary.dissolvedAndTotal?.totalDissolvedAndTotalApprovedSamples ?? 0,
			rejected: summary.dissolvedAndTotal?.totalDissolvedAndTotalRejectedSamples ?? 0,
		},
		{
			key: 'lq',
			cardTitle: 'Porcentagem aprovação LQ',
			approved: summary.lQValues?.totalLQApprovedSamples ?? 0,
			rejected: summary.lQValues?.totalLQRejectedSamples ?? 0,
		},
		{
			key: 'dqoDbo',
			cardTitle: 'Porcentagem aprovação DQO/DBO',
			approved: summary.dQOandDBO?.totalDQOandDBOApprovedSamples ?? 0,
			rejected: summary.dQOandDBO?.totalDQOandDBORejectedSamples ?? 0,
		},
		{
			key: 'od',
			cardTitle: 'Porcentagem aprovação OD',
			approved: summary.dissolvedOxygen?.totalDissolvedOxApprovedSamples ?? 0,
			rejected: summary.dissolvedOxygen?.totalDissolvedOxRejectedSamples ?? 0,
		},
		{
			key: 'stdCe',
			cardTitle: 'Porcentagem STD/CE',
			approved: summary.dissolvedSolidsToEC?.totalDissolvedSolidsApprovedSamples ?? 0,
			rejected: summary.dissolvedSolidsToEC?.totalDissolvedSolidsRejectedSamples ?? 0,
		},
	]
}
